import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ClerkProvider, SignedIn, SignedOut, RedirectToSignIn } from '@clerk/clerk-react';
import { CssBaseline } from '@mui/material';
import { ThemeContextProvider } from './contexts/ThemeContext';
import ResponsiveLayout from './layout/ResponsiveLayout';
import Overview from './pages/Overview';
import Colors from './pages/Colors';
import Typography from './pages/Typography';
import Spacing from './pages/Spacing';
import Buttons from './pages/Buttons';
import Inputs from './pages/Inputs';
import DataDisplay from './pages/DataDisplay';
import Feedback from './pages/Feedback';
import LayoutPage from './pages/Layout';
import Navigation from './pages/Navigation';
import Financial from './pages/Financial';
import ProcessFlow from './pages/ProcessFlow';
import Modals from './pages/Modals';
import SignInPage from './auth/SignInPage';
import SignUpPage from './auth/SignUpPage';
import Dashboard from './pages/Dashboard';
import ProtectedRoute from './components/ProtectedRoute';
import DomainGuard from './components/DomainGuard';
import ErrorBoundary from './components/ErrorBoundary';

// Get Clerk publishable key
const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

if (!clerkPubKey) {
  throw new Error('Missing Clerk Publishable Key');
}

function App() {
  return (
    <ErrorBoundary>
      <ClerkProvider 
      publishableKey={clerkPubKey}
      appearance={{
        variables: {
          colorPrimary: '#FA9940', // Autobia orange
          colorBackground: '#fff',
          colorText: '#333',
        },
        elements: {
          card: {
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e0e0e0',
          },
          headerTitle: {
            color: '#FA9940',
            fontSize: '24px',
            fontWeight: '600',
          },
          socialButtonsBlockButton: {
            borderColor: '#FA9940',
            color: '#FA9940',
          }
        }
      }}
    >
      <ThemeContextProvider>
        <CssBaseline />
        <Router>
          <Routes>
            {/* Public auth routes */}
            <Route path="/sign-in/*" element={<SignInPage />} />
            <Route path="/sign-up/*" element={<SignUpPage />} />
            
            {/* Protected app routes - SignedIn users */}
            <Route path="/" element={
              <SignedIn>
                <DomainGuard>
                  <ResponsiveLayout>
                    <ProtectedRoute><Overview /></ProtectedRoute>
                  </ResponsiveLayout>
                </DomainGuard>
              </SignedIn>
            } />
            <Route path="/dashboard" element={
              <SignedIn>
                <DomainGuard>
                  <ResponsiveLayout>
                    <ProtectedRoute><Dashboard /></ProtectedRoute>
                  </ResponsiveLayout>
                </DomainGuard>
              </SignedIn>
            } />
            <Route path="/colors" element={
              <SignedIn>
                <DomainGuard>
                  <ResponsiveLayout>
                    <ProtectedRoute><Colors /></ProtectedRoute>
                  </ResponsiveLayout>
                </DomainGuard>
              </SignedIn>
            } />
            <Route path="/typography" element={
              <SignedIn>
                <DomainGuard>
                  <ResponsiveLayout>
                    <ProtectedRoute><Typography /></ProtectedRoute>
                  </ResponsiveLayout>
                </DomainGuard>
              </SignedIn>
            } />
            <Route path="/spacing" element={
              <SignedIn>
                <DomainGuard>
                  <ResponsiveLayout>
                    <ProtectedRoute><Spacing /></ProtectedRoute>
                  </ResponsiveLayout>
                </DomainGuard>
              </SignedIn>
            } />
            <Route path="/buttons" element={
              <SignedIn>
                <DomainGuard>
                  <ResponsiveLayout>
                    <ProtectedRoute><Buttons /></ProtectedRoute>
                  </ResponsiveLayout>
                </DomainGuard>
              </SignedIn>
            } />
            <Route path="/inputs" element={
              <SignedIn>
                <DomainGuard>
                  <ResponsiveLayout>
                    <ProtectedRoute><Inputs /></ProtectedRoute>
                  </ResponsiveLayout>
                </DomainGuard>
              </SignedIn>
            } />
            <Route path="/data-display" element={
              <SignedIn>
                <DomainGuard>
                  <ResponsiveLayout>
                    <ProtectedRoute><DataDisplay /></ProtectedRoute>
                  </ResponsiveLayout>
                </DomainGuard>
              </SignedIn>
            } />
            <Route path="/feedback" element={
              <SignedIn>
                <DomainGuard>
                  <ResponsiveLayout>
                    <ProtectedRoute><Feedback /></ProtectedRoute>
                  </ResponsiveLayout>
                </DomainGuard>
              </SignedIn>
            } />
            <Route path="/layout" element={
              <SignedIn>
                <DomainGuard>
                  <ResponsiveLayout>
                    <ProtectedRoute><LayoutPage /></ProtectedRoute>
                  </ResponsiveLayout>
                </DomainGuard>
              </SignedIn>
            } />
            <Route path="/navigation" element={
              <SignedIn>
                <DomainGuard>
                  <ResponsiveLayout>
                    <ProtectedRoute><Navigation /></ProtectedRoute>
                  </ResponsiveLayout>
                </DomainGuard>
              </SignedIn>
            } />
            <Route path="/financial" element={
              <SignedIn>
                <DomainGuard>
                  <ResponsiveLayout>
                    <ProtectedRoute><Financial /></ProtectedRoute>
                  </ResponsiveLayout>
                </DomainGuard>
              </SignedIn>
            } />
            <Route path="/process-flow" element={
              <SignedIn>
                <DomainGuard>
                  <ResponsiveLayout>
                    <ProtectedRoute><ProcessFlow /></ProtectedRoute>
                  </ResponsiveLayout>
                </DomainGuard>
              </SignedIn>
            } />
            <Route path="/modals" element={
              <SignedIn>
                <DomainGuard>
                  <ResponsiveLayout>
                    <ProtectedRoute><Modals /></ProtectedRoute>
                  </ResponsiveLayout>
                </DomainGuard>
              </SignedIn>
            } />
          </Routes>
          
          {/* Redirect unauthenticated users globally */}
          <SignedOut>
            <RedirectToSignIn />
          </SignedOut>
        </Router>
      </ThemeContextProvider>
      </ClerkProvider>
    </ErrorBoundary>
  );
}

export default App;